import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography,
  ToggleButton, ToggleButtonGroup, Slider, Switch, FormControlLabel, Autocomplete, TextField,
  LinearProgress, Divider, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon, SaveAlt as SaveAltIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';
import { STYLE_PRESETS, STYLE_ORDER, resolveBg, StyleKey } from './charts/chartCommon';
import FanChart from './charts/FanChart';
import AscendanceChart from './charts/AscendanceChart';
import SablierChart from './charts/SablierChart';
import ListChart from './charts/ListChart';
import DescendantTreeChart from './charts/DescendantTreeChart';
import RadialDescendantChart from './charts/RadialDescendantChart';
import { exportSvgToPdf, exportSvgToPng, exportRasterToPdf, downloadDataUrl } from '../utils/exportTree';
import { trackEvent } from '../utils/event';

type Variant = 'arbre' | 'radial' | 'fan' | 'asc' | 'sablier' | 'liste' | 'cartes';
type Format = 'pdf' | 'png';
type Scope = 'whole' | 'person';

type Capture = () => Promise<{ dataUrl: string; width: number; height: number } | null>;

interface Props {
  open: boolean;
  onClose: () => void;
  membres: Membre[];
  relations: Relation[];
  defaultRootId: number | null;
  defaultDark: boolean;
  captureCardTree?: Capture;
}

interface VariantMeta {
  key: Variant;
  label: string;
  scope: 'both' | 'person' | 'whole';
  gen?: boolean;
  angle?: boolean;
  raster?: boolean;
}

const VARIANTS: VariantMeta[] = [
  { key: 'arbre', label: 'Arbre', scope: 'both', gen: true },
  { key: 'radial', label: 'Radial (rond)', scope: 'both', gen: true, angle: true },
  { key: 'liste', label: 'Liste', scope: 'both', gen: true },
  { key: 'cartes', label: 'Cartes (vue actuelle)', scope: 'whole', raster: true },
  { key: 'fan', label: 'Éventail (ancêtres)', scope: 'person', gen: true, angle: true },
  { key: 'asc', label: 'Ascendance', scope: 'person', gen: true },
  { key: 'sablier', label: 'Sablier', scope: 'person' },
];

const genMaxFor = (v: Variant) => (v === 'arbre' || v === 'liste' ? 10 : v === 'radial' ? 6 : 5);

const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

const ExportTreeDialog: React.FC<Props> = ({
  open, onClose, membres, relations, defaultRootId, defaultDark, captureCardTree,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [variant, setVariant] = useState<Variant>('arbre');
  const [scope, setScope] = useState<Scope>('whole');
  const [format, setFormat] = useState<Format>('pdf');
  const [rootId, setRootId] = useState<number | null>(defaultRootId);
  const [angle, setAngle] = useState<180 | 360>(360);
  const [maxGen, setMaxGen] = useState(5);
  const [styleKey, setStyleKey] = useState<StyleKey>('moderne');
  const [exportDark, setExportDark] = useState(defaultDark);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);

  // Réinitialise racine + thème à l'ouverture
  useEffect(() => {
    if (!open) return;
    setRootId(defaultRootId ?? membres[0]?.id ?? null);
    setExportDark(defaultDark);
    setError(null);
  }, [open, defaultRootId, defaultDark, membres]);

  // Borne le nombre de générations au max autorisé par la variante
  useEffect(() => { setMaxGen(m => Math.min(m, genMaxFor(variant))); }, [variant]);

  const style = STYLE_PRESETS[styleKey];
  const variantMeta = VARIANTS.find(v => v.key === variant)!;
  const effScope: Scope = variantMeta.scope === 'both' ? scope : (variantMeta.scope as Scope);
  const needsPerson = effScope === 'person';
  const genMax = genMaxFor(variant);
  const gen = Math.min(maxGen, genMax);

  const rootMembre = useMemo(() => membres.find(m => m.id === rootId) ?? null, [membres, rootId]);
  const personRoot = effScope === 'whole' ? null : rootId;
  const previewBg = variant === 'cartes'
    ? (exportDark ? '#0a0a0a' : '#F9FAF5')
    : resolveBg(style, exportDark);

  const chart = () => {
    if (variant === 'cartes') return null;
    if (needsPerson && rootId == null) return null;
    switch (variant) {
      case 'arbre':
        return <DescendantTreeChart ref={svgRef} rootId={personRoot} membres={membres} relations={relations}
          style={style} isDark={exportDark} maxGen={gen} />;
      case 'radial':
        return <RadialDescendantChart ref={svgRef} rootId={personRoot} membres={membres} relations={relations}
          style={style} isDark={exportDark} maxGen={gen} angle={angle} />;
      case 'liste':
        return <ListChart ref={svgRef} rootId={personRoot} membres={membres} relations={relations}
          style={style} isDark={exportDark} maxGen={gen} />;
      case 'fan':
        return <FanChart ref={svgRef} rootId={rootId!} membres={membres} relations={relations}
          style={style} isDark={exportDark} maxGen={gen} angle={angle} />;
      case 'asc':
        return <AscendanceChart ref={svgRef} rootId={rootId!} membres={membres} relations={relations}
          style={style} isDark={exportDark} maxGen={gen} />;
      case 'sablier':
        return <SablierChart ref={svgRef} rootId={rootId!} membres={membres} relations={relations}
          style={style} isDark={exportDark} />;
      default:
        return null;
    }
  };

  const handleDownload = async () => {
    if (variant !== 'cartes' && needsPerson && rootId == null) return;
    setRendering(true);
    setError(null);
    const scopeSuffix = variant === 'cartes' || effScope === 'whole'
      ? 'complet'
      : slug(rootMembre?.prenom ?? 'personne');
    const base = `arbre-${variant}-${scopeSuffix}`;
    try {
      if (variant === 'cartes') {
        const cap = await captureCardTree?.();
        if (!cap) throw new Error('capture indisponible');
        if (format === 'png') downloadDataUrl(cap.dataUrl, `${base}.png`);
        else await exportRasterToPdf(cap.dataUrl, cap.width, cap.height, `${base}.pdf`);
      } else {
        const el = svgRef.current;
        if (!el) throw new Error('aperçu indisponible');
        if (format === 'pdf') await exportSvgToPdf(el, `${base}.pdf`);
        else await exportSvgToPng(el, `${base}.png`, 2);
      }
      trackEvent('Export', variantMeta.label, { props: { format, style: styleKey, variant, scope: effScope } });
    } catch {
      setError("L'export a échoué. Réessayez ou changez de variante.");
    } finally {
      setRendering(false);
    }
  };

  const scopeHint = variantMeta.scope === 'person'
    ? 'Vue d’une lignée — centrée sur une personne.'
    : variantMeta.scope === 'whole'
      ? 'Toute la famille : l’arbre entier tel qu’affiché à l’écran.'
      : 'Toute la famille ou une seule personne, au choix ci-dessous.';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Exporter l'arbre
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Contrôles */}
          <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Field label="Variante">
              <ToggleButtonGroup value={variant} exclusive size="small"
                onChange={(_, v) => v && setVariant(v)} sx={{ flexWrap: 'wrap' }}>
                {VARIANTS.map(v => (
                  <ToggleButton key={v.key} value={v.key} disabled={v.raster && !captureCardTree}
                    sx={{ textTransform: 'none', px: 1.4 }}>{v.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {scopeHint}
              </Typography>
            </Field>

            {variantMeta.scope === 'both' && (
              <Field label="Portée">
                <ToggleButtonGroup value={scope} exclusive size="small" onChange={(_, v) => v && setScope(v)}>
                  <ToggleButton value="whole" sx={{ textTransform: 'none' }}>Toute la famille</ToggleButton>
                  <ToggleButton value="person" sx={{ textTransform: 'none' }}>Une personne</ToggleButton>
                </ToggleButtonGroup>
              </Field>
            )}

            <Field label="Format">
              <ToggleButtonGroup value={format} exclusive size="small" onChange={(_, v) => v && setFormat(v)}>
                <ToggleButton value="pdf" sx={{ textTransform: 'none', gap: 0.5 }}><PdfIcon fontSize="small" /> PDF</ToggleButton>
                <ToggleButton value="png" sx={{ textTransform: 'none', gap: 0.5 }}><ImageIcon fontSize="small" /> PNG</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {variant === 'cartes'
                  ? 'Vue à cartes : image raster (non vectorielle).'
                  : format === 'pdf' ? 'PDF vectoriel : net au zoom, texte sélectionnable.' : 'PNG haute résolution (2×).'}
              </Typography>
            </Field>

            {variant !== 'cartes' && (
              <>
                {needsPerson && (
                  <Field label="Personne racine">
                    <Autocomplete
                      size="small"
                      options={membres}
                      value={rootMembre}
                      onChange={(_, m) => setRootId(m?.id ?? null)}
                      getOptionLabel={m => `${m.prenom} ${m.nom}`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={params => <TextField {...params} placeholder="Choisir…" />}
                    />
                  </Field>
                )}

                {variantMeta.angle && (
                  <Field label="Forme">
                    <ToggleButtonGroup value={angle} exclusive size="small" onChange={(_, v) => v && setAngle(v)}>
                      <ToggleButton value={360} sx={{ textTransform: 'none' }}>Cercle plein</ToggleButton>
                      <ToggleButton value={180} sx={{ textTransform: 'none' }}>Demi-cercle</ToggleButton>
                    </ToggleButtonGroup>
                  </Field>
                )}

                {variantMeta.gen && (
                  <Field label={`Générations : ${gen}`}>
                    <Slider value={gen} min={2} max={genMax} step={1} marks size="small"
                      onChange={(_, v) => setMaxGen(v as number)} />
                  </Field>
                )}

                <Field label="Style">
                  <ToggleButtonGroup value={styleKey} exclusive size="small"
                    onChange={(_, v) => v && setStyleKey(v)} sx={{ flexWrap: 'wrap' }}>
                    {STYLE_ORDER.map(k => (
                      <ToggleButton key={k} value={k} sx={{ textTransform: 'none', px: 1.4 }}>
                        {STYLE_PRESETS[k].name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {style.desc}
                  </Typography>
                </Field>

                <FormControlLabel
                  control={<Switch checked={exportDark} onChange={e => setExportDark(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Fond sombre</Typography>}
                />
              </>
            )}
          </Box>

          <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />

          {/* Aperçu */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">Aperçu</Typography>
            <Box sx={{
              mt: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
              bgcolor: previewBg, p: 2, minHeight: 320, maxHeight: 460, overflow: 'auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '& svg': { maxWidth: '100%', height: 'auto', display: 'block' },
            }}>
              {variant === 'cartes' ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  La vue à cartes actuellement affichée à l'écran sera exportée telle quelle
                  (arbre complet, au-delà de la zone visible).
                </Typography>
              ) : needsPerson && rootId == null ? (
                <Typography variant="body2" color="text.secondary">Choisissez une personne racine.</Typography>
              ) : chart()}
            </Box>
            {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
          </Box>
        </Box>
      </DialogContent>

      {rendering && <LinearProgress />}
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Fermer</Button>
        <Button onClick={handleDownload} variant="contained" size="small" disableElevation
          startIcon={<SaveAltIcon />} disabled={rendering || (variant !== 'cartes' && needsPerson && rootId == null)}>
          Télécharger
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75, display: 'block' }}>
      {label}
    </Typography>
    {children}
  </Box>
);

export default ExportTreeDialog;
